import { Injectable } from '@angular/core';
import L from 'leaflet';
import { PIN_HEIGHT, PIN_WIDTH } from './place-pin';

/** Business ownership stays with the feature; only display movement is shared. */
export interface PlaceMarkerOwner {
  readonly key: string;
  readonly group: L.MarkerClusterGroup;
  readonly color: string;
}

interface MapPins {
  readonly markers: Map<L.Marker, RegisteredMarker>;
  readonly displayed: Map<L.Marker, DisplayOffset>;
  readonly groups: Set<L.MarkerClusterGroup>;
  readonly schedule: () => void;
  frame: number | null;
  zooming: boolean;
  animation: number | null;
  animateNextLayout: boolean;
}

interface RegisteredMarker {
  readonly owner: PlaceMarkerOwner;
  /** Business IDs are numeric; server clusters use their stable tile IDs. */
  readonly id: number | string;
}

interface DisplayOffset {
  readonly owner: PlaceMarkerOwner;
  readonly offset: L.Point;
  connector?: SVGSVGElement;
}

interface VisibleMarker {
  readonly marker: L.Marker;
  readonly registration: RegisteredMarker;
  readonly point: L.Point;
  readonly bounds: L.Bounds;
  readonly hitBounds: L.Bounds;
}

@Injectable({ providedIn: 'root' })
export class PlaceMarkerLayoutService {
  private readonly maps = new WeakMap<L.Map, MapPins>();

  addMarker(map: L.Map, marker: L.Marker, owner: PlaceMarkerOwner, id: number | string): void {
    const pins = this.maps.get(map) ?? this.create(map);
    if (pins.markers.has(marker)) return;
    pins.markers.set(marker, { owner, id });
    marker.on('move', pins.schedule);
    if (!pins.groups.has(owner.group)) {
      pins.groups.add(owner.group);
      owner.group.on('animationend', pins.schedule);
    }
    pins.schedule();
  }

  removeMarker(map: L.Map | undefined, marker: L.Marker): void {
    const pins = map && this.maps.get(map);
    if (!pins) return;
    marker.off('move', pins.schedule);
    this.clearDisplay(marker, pins.displayed.get(marker));
    pins.markers.delete(marker);
    pins.displayed.delete(marker);
    pins.schedule();
  }

  clear(map: L.Map | undefined, owner: PlaceMarkerOwner): void {
    const pins = map && this.maps.get(map);
    if (!pins) return;
    for (const [marker, registration] of pins.markers) {
      if (registration.owner !== owner) continue;
      marker.off('move', pins.schedule);
      this.applyOffset(marker, L.point(0, 0));
      pins.markers.delete(marker);
    }
    for (const [marker, display] of pins.displayed) {
      if (display.owner !== owner) continue;
      this.clearDisplay(marker, display);
      pins.displayed.delete(marker);
    }
    owner.group.off('animationend', pins.schedule);
    pins.groups.delete(owner.group);
    pins.schedule();
  }

  /** Leaflet can recreate the icon when its selection style changes. */
  refreshMarker(map: L.Map | undefined, marker: L.Marker): void {
    const pins = map && this.maps.get(map);
    if (!pins) return;
    const display = pins.displayed.get(marker);
    this.applyOffset(marker, display?.offset ?? L.point(0, 0), !!display);
    if (display) this.updateConnector(marker, display);
    pins.schedule();
  }

  private create(map: L.Map): MapPins {
    const pins: MapPins = {
      markers: new Map(), displayed: new Map(), groups: new Set(), frame: null, zooming: false,
      animation: null, animateNextLayout: false,
      schedule: () => {
        if (this.maps.get(map) !== pins || pins.zooming || pins.animation !== null || pins.frame !== null) return;
        pins.frame = requestAnimationFrame(() => {
          pins.frame = null;
          // MarkerCluster starts its own animation in zoomend handlers. Wait
          // for every group, including groups whose handlers ran after ours.
          const clustering = [...pins.groups].some(group =>
            ((group as L.MarkerClusterGroup & { _inZoomAnimation?: number })._inZoomAnimation ?? 0) > 0);
          if (this.maps.get(map) === pins && !pins.zooming && !clustering) this.layout(map, pins);
        });
      },
    };
    this.maps.set(map, pins);
    const zoomStarted = () => {
      pins.zooming = true;
      // Retain the currently displayed positions, including an interrupted
      // layout transition. Leaflet moves each icon and its connector together.
      if (pins.animation !== null) window.cancelAnimationFrame(pins.animation);
      pins.animation = null;
    };
    const settled = () => {
      pins.zooming = false;
      pins.animateNextLayout = !!map.options.zoomAnimation
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      pins.schedule();
    };
    map.on('zoomstart', zoomStarted);
    map.on('zoomend', settled);
    map.on('moveend resize layeradd layerremove', pins.schedule);
    map.once('unload', () => {
      this.maps.delete(map);
      if (pins.frame !== null) window.cancelAnimationFrame(pins.frame);
      if (pins.animation !== null) window.cancelAnimationFrame(pins.animation);
      map.off('zoomstart', zoomStarted);
      map.off('zoomend', settled);
      map.off('moveend resize layeradd layerremove', pins.schedule);
      for (const group of pins.groups) group.off('animationend', pins.schedule);
      for (const marker of pins.markers.keys()) marker.off('move', pins.schedule);
      this.resetOffsets(pins);
      pins.markers.clear();
      pins.groups.clear();
    });
    return pins;
  }

  private layout(map: L.Map, pins: MapPins): void {
    const visible: VisibleMarker[] = [];
    const viewport = L.bounds([0, 0], map.getSize());
    let cellSize = Math.max(PIN_WIDTH, PIN_HEIGHT);
    // Leaflet owns clustering. Collect each currently displayed parent once,
    // including standalone server representatives, without laying out its children.
    const candidates = new Map<L.Marker, RegisteredMarker>();
    for (const [leaf, registration] of pins.markers) {
      if (!map.hasLayer(registration.owner.group)) continue;
      const marker = registration.owner.group.getVisibleParent(leaf);
      if (!marker) continue;
      const previous = candidates.get(marker);
      if (!previous || this.compareIdentity(registration, previous) < 0) {
        candidates.set(marker, registration);
      }
    }
    for (const [marker, registration] of candidates) {
      const element = marker.getElement();
      if (!map.hasLayer(marker) || !element?.isConnected) continue;
      const margin = L.point(parseFloat(element.style.marginLeft) || 0,
        parseFloat(element.style.marginTop) || 0);
      const point = map.latLngToContainerPoint(marker.getLatLng());
      const corner = point.add(margin);
      const painted = this.paintedBounds(element);
      const bounds = L.bounds(corner.add(painted.min!), corner.add(painted.max!));
      if (!bounds.overlaps(viewport)) continue;
      const hitBounds = L.bounds(corner, corner.add([element.offsetWidth, element.offsetHeight]));
      visible.push({ marker, registration, point, bounds, hitBounds });
      cellSize = Math.max(cellSize, bounds.getSize().x, bounds.getSize().y);
    }

    // Index original locations to find actual collisions. Isolated neighbours
    // stay reserved so resolving a collision cannot displace them.
    const cells = new Map<string, Set<VisibleMarker>>();
    const occupied = new Map(visible.map(pin => [pin, pin.bounds]));
    const visitCells = (bounds: L.Bounds, visit: (key: string) => void) => {
      for (let x = Math.floor(bounds.min!.x / cellSize); x <= Math.floor(bounds.max!.x / cellSize); x++) {
        for (let y = Math.floor(bounds.min!.y / cellSize); y <= Math.floor(bounds.max!.y / cellSize); y++) {
          visit(`${x}/${y}`);
        }
      }
    };
    const insert = (pin: VisibleMarker, bounds: L.Bounds) => visitCells(bounds, key => {
      const members = cells.get(key) ?? new Set<VisibleMarker>();
      members.add(pin);
      cells.set(key, members);
    });
    const collisions = (pin: VisibleMarker, bounds: L.Bounds): L.Bounds[] => {
      const found = new Set<VisibleMarker>();
      visitCells(bounds, key => {
        for (const other of cells.get(key) ?? []) {
          if (other !== pin && bounds.overlaps(occupied.get(other)!)) found.add(other);
        }
      });
      return [...found].map(other => occupied.get(other)!);
    };
    for (const pin of visible) insert(pin, pin.bounds);
    const offsets = new Map<L.Marker, DisplayOffset>();
    const overlapping = visible.filter(pin => collisions(pin, pin.bounds).length > 0);
    // Original geometry and identity determine order; selection and previous
    // display offsets never influence the result.
    overlapping.sort((a, b) => b.point.x - a.point.x || b.point.y - a.point.y
      || this.compareIdentity(b.registration, a.registration));
    // Place conflicting symbols once in stable order. Do not reserve all of
    // their old positions: that would trap a chain's middle symbol and force a
    // large detour even when a few pixels per symbol would be enough.
    for (const pin of overlapping) visitCells(pin.bounds, key => cells.get(key)!.delete(pin));

    for (const pin of overlapping) {
      let offset = L.point(0, 0);
      if (collisions(pin, pin.bounds).length) {
        // Find the first free position along each axis, then take the shortest.
        // Each step clears at least one blocker; no fixed fan-out or lift is used.
        let bestDistance = Infinity;
        for (const [axis, direction] of [['x', -1], ['x', 1], ['y', -1], ['y', 1]] as const) {
          const candidate = L.point(0, 0);
          let blockers = collisions(pin, pin.bounds);
          while (blockers.length && Math.abs(candidate[axis]) < bestDistance) {
            const distances = blockers.map(bounds => direction < 0
              ? bounds.min![axis] - pin.bounds.max![axis]
              : bounds.max![axis] - pin.bounds.min![axis]);
            candidate[axis] = direction < 0
              ? Math.floor(Math.min(...distances)) : Math.ceil(Math.max(...distances));
            blockers = collisions(pin, L.bounds(pin.bounds.min!.add(candidate), pin.bounds.max!.add(candidate)));
          }
          if (!blockers.length && Math.abs(candidate[axis]) < bestDistance) {
            offset = candidate;
            bestDistance = Math.abs(candidate[axis]);
          }
        }
      }
      const moved = L.bounds(pin.bounds.min!.add(offset), pin.bounds.max!.add(offset));
      occupied.set(pin, moved);
      insert(pin, moved);
      offsets.set(pin.marker, { owner: pin.registration.owner, offset });
    }
    // Check final positions: a displaced symbol can end up beside an isolated
    // neighbour's transparent padding even if their original icons were apart.
    for (const pin of visible) {
      if (!offsets.has(pin.marker) && collisions(pin, pin.hitBounds).length) {
        offsets.set(pin.marker, { owner: pin.registration.owner, offset: L.point(0, 0) });
      }
    }
    const animate = pins.animateNextLayout;
    pins.animateNextLayout = false;
    if (animate) this.transition(map, pins, offsets);
    else this.render(pins, offsets);
  }

  private render(pins: MapPins, offsets: Map<L.Marker, DisplayOffset>): void {
    for (const [marker, display] of pins.displayed) {
      if (!offsets.has(marker)) this.clearDisplay(marker, display);
    }
    for (const [marker, display] of offsets) {
      display.connector = pins.displayed.get(marker)?.connector;
      this.applyOffset(marker, display.offset, true);
      this.updateConnector(marker, display);
    }
    pins.displayed.clear();
    for (const [marker, display] of offsets) pins.displayed.set(marker, display);
  }

  private transition(map: L.Map, pins: MapPins, targets: Map<L.Marker, DisplayOffset>): void {
    const from = new Map(pins.displayed);
    const moving = new Map(targets);
    for (const [marker, display] of from) {
      // A surviving symbol may now fit at its original location. Ease back
      // before dropping its connector; hidden/replaced symbols are just removed.
      if (!moving.has(marker) && map.hasLayer(marker) && map.hasLayer(display.owner.group)) {
        moving.set(marker, { owner: display.owner, offset: L.point(0, 0) });
      }
    }
    if ([...moving].every(([marker, target]) =>
      target.offset.equals(from.get(marker)?.offset ?? L.point(0, 0)))) {
      this.render(pins, targets);
      return;
    }
    let started: number | undefined;
    const step = (time: number) => {
      if (this.maps.get(map) !== pins || pins.zooming) return;
      started ??= time;
      const progress = Math.min(1, (time - started) / 200);
      const eased = 1 - (1 - progress) ** 3;
      const frame = new Map<L.Marker, DisplayOffset>();
      for (const [marker, target] of moving) {
        if (!map.hasLayer(marker) || !map.hasLayer(target.owner.group)) continue;
        const origin = from.get(marker)?.offset ?? L.point(0, 0);
        frame.set(marker, {
          owner: target.owner,
          offset: origin.add(target.offset.subtract(origin).multiplyBy(eased)),
        });
      }
      this.render(pins, frame);
      if (progress < 1) pins.animation = requestAnimationFrame(step);
      else {
        pins.animation = null;
        // Coalesce any data/visibility changes that arrived during the transition.
        pins.schedule();
      }
    };
    pins.animation = requestAnimationFrame(step);
  }

  private paintedBounds(element: HTMLElement): L.Bounds {
    // Read only the body, never the selection outline or translated DOM box.
    // SVG supplies the geometry and scale for Pins and all cluster icon sizes.
    const body = element.querySelector<SVGGraphicsElement>(
      '.map-feature-marker-pin__body, .map-feature-cluster-symbol__body');
    const matrix = body?.getCTM();
    if (!body || !matrix) return L.bounds([0, 0], [element.offsetWidth, element.offsetHeight]);
    const box = body.getBBox();
    const points = [
      new DOMPoint(box.x, box.y), new DOMPoint(box.x + box.width, box.y),
      new DOMPoint(box.x, box.y + box.height), new DOMPoint(box.x + box.width, box.y + box.height),
    ].map(point => point.matrixTransform(matrix)).map(point => L.point(point.x, point.y));
    const bounds = L.bounds(points);
    // Two pixels on each side leave a small, selection-independent visual gap.
    return L.bounds(bounds.min!.subtract([2, 2]), bounds.max!.add([2, 2]));
  }

  private updateConnector(marker: L.Marker, display: DisplayOffset): void {
    const element = marker.getElement();
    if (!element) return;
    const { offset } = display;
    if (!offset.x && !offset.y) {
      display.connector?.remove();
      display.connector = undefined;
      return;
    }
    // These endpoints are screen offsets, not geographic geometry. Keeping the
    // line inside its icon gives it the very same native zoom/cluster transform,
    // instead of scaling a geographic Polyline independently of a fixed-size Pin.
    const ns = 'http://www.w3.org/2000/svg';
    if (!display.connector) {
      const svg = document.createElementNS(ns, 'svg');
      svg.classList.add('map-marker-stack-connector');
      svg.setAttribute('width', '1');
      svg.setAttribute('height', '1');
      svg.setAttribute('aria-hidden', 'true');
      const line = document.createElementNS(ns, 'line');
      line.setAttribute('stroke', display.owner.color);
      line.setAttribute('stroke-width', '1.5');
      line.setAttribute('stroke-dasharray', '0 4');
      line.setAttribute('stroke-linecap', 'round');
      svg.append(line);
      display.connector = svg;
    }
    if (display.connector.parentElement !== element) element.prepend(display.connector);
    const line = display.connector.firstElementChild!;
    const x = -(parseFloat(element.style.marginLeft) || 0);
    const y = -(parseFloat(element.style.marginTop) || 0);
    line.setAttribute('x1', String(x - offset.x));
    line.setAttribute('y1', String(y - offset.y));
    line.setAttribute('x2', String(x));
    line.setAttribute('y2', String(y));
  }

  private clearDisplay(marker: L.Marker, display: DisplayOffset | undefined): void {
    display?.connector?.remove();
    this.applyOffset(marker, L.point(0, 0));
  }

  private compareIdentity(a: RegisteredMarker, b: RegisteredMarker): number {
    return a.owner.key.localeCompare(b.owner.key)
      || (typeof a.id === 'number' && typeof b.id === 'number'
        ? a.id - b.id : String(a.id).localeCompare(String(b.id)));
  }

  private resetOffsets(pins: MapPins): void {
    for (const [marker, display] of pins.displayed) this.clearDisplay(marker, display);
    pins.displayed.clear();
  }

  private applyOffset(marker: L.Marker, offset: L.Point, stacked = false): void {
    const element = marker.getElement();
    if (!element) return;
    // Individual translate composes with Leaflet's position/zoom transform.
    // The whole hit target and its selection outline move together; geographic
    // coordinates and MarkerCluster's spatial index remain untouched.
    const translate = offset.y ? `${offset.x}px ${offset.y}px` : offset.x ? `${offset.x}px` : '';
    if (element.style.translate !== translate) element.style.translate = translate;
    element.classList.toggle('is-stacked', stacked);
  }

}
